// SKILL FUSIONS (owner 2026-11-02): HUD pieces — the emerald FUSION draft card (partner icons → result), the fused build slot,
// the pause-planner card and the fusion bonds. Reads the Sim; writes nothing.
import { MAX_WEAPON_LEVEL, WEAPONS } from '../core/content';
import { FUSIONS, evoMask, fusionCardRows, recipesWith, resonance, type FusionId } from '../core/fusions';
import type { Sim } from '../core/sim';
import { fusionIcon, techIcon } from './icons';

/** the evolved-partner count a fusion made now would carry (Resonance) */
export function pendingEvo(sim: Sim, id: FusionId): number { const f = FUSIONS[id]; return evoMask(sim.evolved.has(f.a), sim.evolved.has(f.b)); }
const evoOf = (sim: Sim, id: FusionId): number => sim.fusion.owned.get(id)?.evo ?? pendingEvo(sim, id);
/** the build's multipliers as the skill cards show them (no Surge), with this fusion's Attunement */
const cardMods = (sim: Sim, id: FusionId) => ({ cdMul: sim.baseCdMul, dmgMul: sim.baseDmgMul * (1 + (sim.attuneDmg[id] ?? 0)), areaMul: sim.areaMul });

/** stat chips (same markup as the skill cards' statGrid) */
export function fusionStatGrid(sim: Sim, id: FusionId, evo = evoOf(sim, id)): string {
  const rows = fusionCardRows(id, cardMods(sim, id), evo);
  const cells = rows.filter((r) => r.k !== 'extra').map((r) => `<div class="st"><i>${r.label}</i><span class="v"><b>${r.v}</b></span></div>`).join('');
  const extra = rows.find((r) => r.k === 'extra');
  return `<div class="stats">${cells}</div>${extra ? `<div class="snote">${extra.v}</div>` : ''}`;
}

/** card icon: partner A + partner B → the fusion */
export function fusionCardIcon(sim: Sim | undefined, id: FusionId): string {
  const f = FUSIONS[id];
  const p = (w: typeof f.a) => `<span class="fu-p">${techIcon(w, !!sim?.evolved.has(w))}</span>`;
  return `<span class="fu-recipe">${p(f.a)}<span class="fu-plus">+</span>${p(f.b)}<span class="fu-arrow">➜</span><span class="fu-res">${fusionIcon(id)}</span></span>`;
}

/** extra line under a fusion draft card */
export function fusionCardNote(sim: Sim, id: FusionId): string {
  const f = FUSIONS[id], evo = pendingEvo(sim, id);
  const nm = (w: typeof f.a) => (sim.evolved.has(w) ? WEAPONS[w].evolvedName : WEAPONS[w].name);
  return `<div class="req ok fu-req">Fuses ${nm(f.a)} + ${nm(f.b)} into one slot (frees a slot)${evo ? ` · Resonance ×${resonance(id, evo).toFixed(2)} from evolved partners` : ''}. Skip it and it comes back next level-up.</div>`;
}

/** HUD build-slot chip for an owned fusion */
export function fusionSlot(sim: Sim, id: FusionId): string {
  const rows = fusionCardRows(id, cardMods(sim, id), evoOf(sim, id)).filter((r) => r.k !== 'extra').map((r) => `${r.label} ${r.v}`).join(' · ');
  return `<div class="slot evo fusion" title="${FUSIONS[id].name} (fusion) — ${rows}"><span>${fusionIcon(id)}</span><i>✦</i></div>`;
}

/** pause planner card */
export function fusionPlannerCard(sim: Sim, id: FusionId, total: number): string {
  const f = FUSIONS[id], d = sim.dmgBySrc[id] ?? 0, evo = evoOf(sim, id);
  const dmg = d > 0 ? `<p class="bp-dmg">Damage dealt: <b>${Math.round(d).toLocaleString()}</b> (${Math.round((d / total) * 100)}% of total)</p>` : '';
  return `<div class="bp-card evolve fusion"><div class="bp-ico">${fusionIcon(id)}</div>`
    + `<div class="bp-txt"><div class="bp-h"><h3>✦ ${f.name}</h3><span class="fu-tag">FUSION</span></div>`
    + `<p class="bp-plan">${WEAPONS[f.a].name} + ${WEAPONS[f.b].name}${evo ? ` · Resonance ×${resonance(id, evo).toFixed(2)}` : ''}</p>`
    + `<p class="bp-plan">${f.desc}</p>${fusionStatGrid(sim, id, evo)}${dmg}</div></div>`;
}

/** Fusion bonds for the Bond Lattice: every recipe with at least one partner held (owned fusions first). */
export function fusionBonds(sim: Sim): string {
  const seen = new Set<FusionId>(), out: string[] = [];
  for (const id of sim.fusion.owned.keys()) { seen.add(id); out.push(bond(sim, id)); }
  for (const w of sim.weapons.keys()) for (const f of recipesWith(w)) if (!seen.has(f.id)) { seen.add(f.id); out.push(bond(sim, f.id)); }
  return out.length ? `<div class="lat-head">FUSIONS</div><div class="bonds fu-bonds">${out.join('')}</div>` : '';
}
function bond(sim: Sim, id: FusionId): string {
  const f = FUSIONS[id], own = sim.fusion.has(id);
  const lv = (w: typeof f.a) => sim.weapons.get(w) ?? 0;
  const part = (w: typeof f.a) => `<span class="bt ${own || lv(w) >= MAX_WEAPON_LEVEL ? 'ok' : lv(w) > 0 ? '' : 'miss'}">${techIcon(w, sim.evolved.has(w))}<em>${own ? '✦' : lv(w) || '–'}</em></span>`;
  const ready = sim.canFuse(id);
  const state = own ? 'FUSED' : ready ? 'READY' : `${WEAPONS[f.a].name} ${lv(f.a)}/${MAX_WEAPON_LEVEL} · ${WEAPONS[f.b].name} ${lv(f.b)}/${MAX_WEAPON_LEVEL}`;
  return `<div class="bond fu ${own ? 'done' : ready ? 'ready' : 'wait'}">${part(f.a)}<span class="plus">+</span>${part(f.b)}<span class="arrow">➜</span>`
    + `<span class="bn"><strong>✦ ${f.name}</strong><small>${state}</small></span></div>`;
}
